import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';

import type { OrganisationClient } from '~/api/clients/types';

import { useUpdateClient } from '~/api/clients';
import { Blocker, Container, Header } from '~/components';
import { Field, Form, Input } from '~/components/Form';
import { europeanUnionCountryCodes } from '~/constants/countries';
import { bypassBlocker } from '~/constants/router';
import { SearchParamKeys } from '~/constants/url';
import { r, routes } from '~/providers/RouterProvider/router.routes';
import { includes } from '~/utils/arrays';
import { isNotBlank } from '~/utils/string';
import { toast } from '~/utils/toast';

import type { EditClientFormType } from './types';

import { FormAddressCity } from '../components/FormAddressCity';
import { FormAddressNumber } from '../components/FormAddressNumber';
import { FormAddressStreet } from '../components/FormAddressStreet';
import { FormAddressZipCode } from '../components/FormAddressZipCode';
import { FormCompanyName } from '../components/FormCompanyName';
import { FormCountryCode } from '../components/FormCountryCode';
import { gridStyle } from './EditClientForm.css';

type Props = {
  client: OrganisationClient;
};

export const EditClientForm = ({ client }: Props) => {
  const mutation = useUpdateClient(client.id);

  const [address] = client.addresses;

  const {
    control,
    formState: { isDirty },
    handleSubmit,
  } = useForm<EditClientFormType>({
    defaultValues: {
      companyName: client.name,
      otherNumber: client.otherNumber ?? '',
      address: {
        street: address.street,
        number: address.number,
        zipCode: address.zipCode,
        city: address.city,
        countryCode: address.countryCode,
      },
    },
  });

  const navigate = useNavigate();
  const searchParams = useSearchParams()[0];
  const redirectPath = searchParams.get(SearchParamKeys.REDIRECT_PATH);
  const { t } = useTranslation(['clients', 'common', 'validation']);

  const isBelgianOrganisation = address.countryCode === 'BE';
  const isEuropeanOrganisation = !isBelgianOrganisation && includes(europeanUnionCountryCodes, address.countryCode);
  const isInternationalOrganisation = !isBelgianOrganisation && !isEuropeanOrganisation;
  const hasOtherNumber = (isBelgianOrganisation && !client.vatNumber) || isEuropeanOrganisation || isInternationalOrganisation;

  const onSubmit = handleSubmit((data) => {
    if (mutation.isPending) return;

    mutation.mutate(data, {
      onSuccess: () => {
        toast.success({ description: t('clients:alerts.successUpdated') });
        const redirectRoute = redirectPath ?? r(routes.showClient, { clientId: client.id });
        navigate(redirectRoute, { state: bypassBlocker });
      },
      onError: () => toast.danger({ description: t('common:error') }),
    });
  });

  return (
    <>
      <Header.Root>
        <Header.Content backTo={r(routes.showClient, { clientId: client.id })} title={client.name} />

        <Header.Actions>
          <Header.Action icon="Save" loading={mutation.isPending} onClick={onSubmit}>
            {t('common:actions.save')}
          </Header.Action>
        </Header.Actions>
      </Header.Root>

      <Blocker isBlocked={isDirty} message={t('clients:editClient.blocker')} />

      <Container>
        <Form autoComplete="off" className={gridStyle} onSubmit={onSubmit}>
          <FormCompanyName control={control} />

          {client.vatNumber && (
            <Field.Root disabled name="vatNumber">
              <Field.Label>{t('clients:fields.vatNumber.label')}</Field.Label>
              <Input value={client.vatNumber} />
            </Field.Root>
          )}

          {hasOtherNumber && (
            <Controller
              control={control}
              name="otherNumber"
              render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
                <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
                  <Field.Label optional={!!client.vatNumber}>
                    {isInternationalOrganisation ? t('clients:fields.vatOrOtherNumber.label') : t('clients:fields.otherNumber.label')}
                  </Field.Label>

                  <Input
                    onBlur={onBlur}
                    onValueChange={onChange}
                    placeholder={t('clients:fields.otherNumber.placeholder')}
                    ref={ref}
                    value={value ?? ''}
                  />

                  <Field.Error match={!!error}>{error?.message}</Field.Error>
                </Field.Root>
              )}
              rules={{
                required: { value: !client.vatNumber, message: t('validation:required') },
                validate: { notBlank: (value) => !!client.vatNumber || isNotBlank(value ?? '') || t('validation:required') },
              }}
            />
          )}

          <FormAddressStreet control={control} />
          <FormAddressNumber control={control} />
          <FormAddressZipCode control={control} />
          <FormAddressCity control={control} />
          <FormCountryCode control={control} disabled />
        </Form>
      </Container>
    </>
  );
};
